/**
 * The primitives every new page is built from (ui/DESIGN.md, "Contracts"): one surface language, one
 * type scale, one accent. Colour comes from design/tokens.css only; status is a dot, a ring or text.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { count } from '../lib/format.ts';
import { systemCode, systemColor, systemIcon, systemLogo, systemName } from '../lib/systems.ts';
import { CloseIcon, DocumentIcon, RecordIcon, RunIcon, SYSTEM_ICON } from '../components/icons.tsx';

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

/**
 * A data model as people would say it: ErpSalesOrder → sales order(s). A copy of `noun()` in
 * components/data/Journey.tsx (the source of truth until that file is retired), kept here so the
 * shell does not pull the old map engine in.
 */
import { noun } from '../lib/nouns.ts';
export { noun };

// ---------------------------------------------------------------- surfaces and text

/** A card on the page (level 1: --n2 on --n1, no border) or something that floats (level 2: with the one shadow). */
export function Surface({ level = 1, radius = 'card', className, children, ...rest }: {
  level?: 1 | 2; radius?: 'stage' | 'card'; className?: string; children?: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cx('bg-n2 text-n10', radius === 'stage' ? 'rounded-stage' : 'rounded-card', level === 2 && 'shadow-float', className)}>
      {children}
    </div>
  );
}

/** Micro, uppercase, tracked: the small word above a thing that says what it is ("WHERE · 2 addresses"). */
export function Kicker({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx('text-micro font-medium uppercase tracking-[.08em] text-n7', className)}>{children}</div>;
}

const NUM: Record<'display' | 'title' | 'body', string> = {
  display: 'text-display font-medium',
  title: 'text-title font-semibold',
  body: 'text-body font-medium',
};

/** A number set in tabular numerals (fi-FI grouping: 1 284). Label it with what it counts next to it. */
export function Num({ value, size = 'body', className }: { value: number | null | undefined; size?: 'display' | 'title' | 'body'; className?: string }) {
  return <span className={cx('tabular-nums', NUM[size], className)}>{count(value)}</span>;
}

// ---------------------------------------------------------------- chips and pills

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'fail';
const CHIP: Record<Tone, string> = {
  neutral: 'bg-n3 text-n9',
  accent: 'bg-accent-soft text-accent',
  ok: 'bg-n3 text-n9',
  warn: 'bg-n3 text-n9',
  fail: 'bg-n3 text-n9',
};
const DOT: Partial<Record<Tone, string>> = { ok: 'bg-ok', warn: 'bg-warn', fail: 'bg-fail' };

/** A value or an insight. Status tones carry a dot, never a fill; `onRemove` adds the small ×. */
export function Chip({ tone = 'neutral', icon, onRemove, onClick, className, children, ...rest }: {
  tone?: Tone; icon?: ReactNode; onRemove?: () => void; onClick?: (e: MouseEvent<HTMLButtonElement>) => void; className?: string; children: ReactNode;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'onClick' | 'children'>) {
  const body = (
    <>
      {DOT[tone] && <i className={cx('size-2 rounded-full shrink-0', DOT[tone])} aria-hidden="true" />}
      {icon}
      <span className="truncate">{children}</span>
    </>
  );
  return (
    <span {...rest} className={cx('inline-flex items-center gap-2 h-8 rounded-chip text-small font-medium whitespace-nowrap max-w-full', onRemove ? 'pr-1.5' : 'pr-3', onClick ? 'pl-0' : 'pl-3', CHIP[tone], className)}>
      {onClick ? (
        <button type="button" onClick={onClick} className="inline-flex items-center gap-2 h-full pl-3 bg-transparent border-0 text-inherit font-inherit cursor-pointer min-w-0 rounded-chip">
          {body}
        </button>
      ) : body}
      {onRemove && (
        <button type="button" aria-label="Remove" onClick={onRemove}
          className="size-5 grid place-items-center rounded-full bg-transparent border-0 text-inherit opacity-60 cursor-pointer hover:opacity-100 hover:bg-n2/60">
          <CloseIcon size={12} />
        </button>
      )}
    </span>
  );
}

/** One of a few choices (a range, a lens). The active one lifts; put several in a `bg-n3 rounded-full p-0.5` track. */
export function Pill({ active, onClick, className, children }: { active?: boolean; onClick?: () => void; className?: string; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={!!active} onClick={onClick}
      className={cx('h-7 px-3 rounded-full text-small font-medium border-0 cursor-pointer whitespace-nowrap transition-colors duration-[var(--t-micro)] ease-mv',
        active ? 'bg-n2 text-n10 shadow-node' : 'bg-transparent text-n8 hover:text-n10', className)}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- systems and nouns

/**
 * A system by its code: a neutral pill with a 1.5 px ring in the system's hue (the registry's colour, lib/systems.ts).
 * With a logo the pill holds the logo (on white, as logos are drawn); with an icon and room (24 px and up) the icon in
 * the system's hue goes before the code; otherwise the code alone, always --micro. The pill grows to fit.
 */
export function SystemBadge({ code, size = 24, className }: { code: string; size?: 20 | 24 | 28; className?: string }) {
  const color = systemColor(code);
  const logo = systemLogo(code);
  const Icon = size >= 24 ? SYSTEM_ICON[systemIcon(code) ?? ''] : undefined;
  const label = `${systemName(code)} (${systemCode(code)})`;
  const pad = size === 20 ? 7 : size === 24 ? 8 : 9;
  if (logo) {
    return (
      <span role="img" aria-label={label} title={label} data-system={code}
        className={cx('inline-grid place-items-center rounded-full bg-white shrink-0 overflow-hidden', className)}
        style={{ height: size, minWidth: size, maxWidth: size * 3, padding: '0 3px', boxShadow: `0 0 0 1.5px ${color}` }}>
        <img src={logo} alt="" draggable={false} className="block object-contain" style={{ height: size - 5, maxWidth: size * 3 - 6 }} />
      </span>
    );
  }
  return (
    <span title={label} data-system={code}
      className={cx('inline-flex items-center justify-center gap-1 rounded-full bg-n2 text-n9 font-mono text-micro font-semibold uppercase shrink-0 leading-none', className)}
      style={{ height: size, minWidth: size, padding: `0 ${pad}px 0 ${Icon ? pad - 2 : pad}px`, letterSpacing: '.04em', boxShadow: `0 0 0 1.5px ${color}` }}>
      {Icon && <Icon size={size === 24 ? 13 : 15} style={{ color }} className="shrink-0" />}
      {systemCode(code)}
    </span>
  );
}

export type Noun = 'run' | 'document' | 'record';
const NOUN: Record<Noun, { label: string; Icon: typeof RunIcon; text: string; soft: string }> = {
  run: { label: 'run', Icon: RunIcon, text: 'text-run', soft: 'bg-run-soft' },
  document: { label: 'document', Icon: DocumentIcon, text: 'text-doc', soft: 'bg-doc-soft' },
  record: { label: 'record', Icon: RecordIcon, text: 'text-rec', soft: 'bg-rec-soft' },
};

/** The icon of a noun in its colour: play-in-circle, page, layered squares. */
export function NounIcon({ kind, size = 16, className }: { kind: Noun; size?: number; className?: string }) {
  const { Icon, text } = NOUN[kind];
  return <Icon size={size} className={cx(text, 'shrink-0', className)} />;
}

/** "run" / "6 documents": the noun as a small tag, with the count when there is one. */
export function NounTag({ kind, count: n, className }: { kind: Noun; count?: number; className?: string }) {
  const { label, Icon, text, soft } = NOUN[kind];
  return (
    <span className={cx('inline-flex items-center gap-1.5 h-6 rounded-full pl-1.5 pr-2.5 text-small font-medium whitespace-nowrap', soft, text, className)}>
      <Icon size={14} />
      {n === undefined ? label : `${count(n)} ${label}${n === 1 ? '' : 's'}`}
    </span>
  );
}

// ---------------------------------------------------------------- the document glyph

export type Format = string | null | undefined;
const MARKS: Record<string, string> = { json: '{ }', xml: '< >', csv: 'a,b', edifact: "'+:", txt: 'Aa', text: 'Aa' };

/**
 * The document glyph, centred on the origin in a 16 × 20 box: the page, its folded corner and the format mark
 * (a text at `markY`, mono, bold, `markSize`). src/map/glyph.tsx draws the same paths in SVG, so a document
 * looks the same moving on the map and sitting in a list.
 */
export function glyphPaths(format: Format) {
  const f = (format ?? '').toLowerCase();
  return {
    page: 'M-6.5,-8.5h8.5l4.5,4.5v12.5h-13z',
    fold: 'M2,-8.5v4.5h4.5',
    mark: MARKS[f] ?? (f ? 'Aa' : ''),
    markY: 4.2,
    markSize: 6.6,
    width: 16,
    height: 20,
  };
}

/** The document icon inline, `size` px tall; failed ones are drawn in --fail. */
export function Glyph({ format, size = 20, failed, className }: { format: Format; size?: number; failed?: boolean; className?: string }) {
  const g = glyphPaths(format);
  return (
    <svg width={(size * g.width) / g.height} height={size} viewBox="-8 -10 16 20" aria-hidden="true"
      className={cx(failed ? 'text-fail' : 'text-doc', 'shrink-0', className)}>
      <path d={g.page} fill="var(--n2)" stroke="currentColor" strokeWidth={1.4} strokeLinejoin="round" />
      <path d={g.fold} fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinejoin="round" />
      <text y={g.markY} textAnchor="middle" fontFamily="var(--mono)" fontSize={g.markSize} fontWeight={700} fill="currentColor">{g.mark}</text>
    </svg>
  );
}

// ---------------------------------------------------------------- buttons and menus

/** A round button with only an icon; `label` names it for assistive tech. */
export function IconButton({ label, onClick, active, className, children, ref, ...rest }: {
  label: string; onClick?: (e: MouseEvent<HTMLButtonElement>) => void; active?: boolean; className?: string; children: ReactNode; ref?: Ref<HTMLButtonElement>;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>) {
  return (
    <button ref={ref} type="button" aria-label={label} onClick={onClick} {...rest}
      className={cx('size-8 grid place-items-center rounded-full border-0 cursor-pointer transition-colors duration-[var(--t-micro)] ease-mv',
        active ? 'bg-n3 text-n10' : 'bg-transparent text-n8 hover:bg-n3 hover:text-n10', className)}>
      {children}
    </button>
  );
}

const menuClass = 'z-50 min-w-52 bg-n2 text-n10 rounded-card shadow-float p-1.5 outline-none mv-pop';
const itemClass = 'flex items-center gap-2.5 h-9 px-3 rounded-chip text-small outline-none cursor-pointer select-none data-[highlighted]:bg-n3 data-[disabled]:opacity-40 data-[disabled]:pointer-events-none';

function MenuContent({ className, children, align = 'end', sideOffset = 8, ...rest }: Dropdown.DropdownMenuContentProps) {
  return (
    <Dropdown.Portal>
      <Dropdown.Content align={align} sideOffset={sideOffset} collisionPadding={12} {...rest} className={cx(menuClass, className)}>{children}</Dropdown.Content>
    </Dropdown.Portal>
  );
}

function MenuSubContent({ className, children, sideOffset = 6, ...rest }: Dropdown.DropdownMenuSubContentProps) {
  return (
    <Dropdown.Portal>
      <Dropdown.SubContent sideOffset={sideOffset} collisionPadding={12} {...rest} className={cx(menuClass, className)}>{children}</Dropdown.SubContent>
    </Dropdown.Portal>
  );
}

/** A row of the menu; `selected` shows the accent dot (pass false to keep the dot's room). */
function MenuItem({ icon, selected, tone = 'neutral', className, children, ...rest }: Dropdown.DropdownMenuItemProps & { icon?: ReactNode; selected?: boolean; tone?: 'neutral' | 'fail' }) {
  return (
    <Dropdown.Item {...rest} className={cx(itemClass, tone === 'fail' ? 'text-fail' : 'text-n10', className)}>
      {selected !== undefined && <span className={cx('size-1.5 rounded-full shrink-0', selected ? 'bg-accent' : 'bg-transparent')} aria-hidden="true" />}
      {icon && <span className={cx('shrink-0 inline-flex', tone === 'fail' ? 'text-fail' : 'text-n8')}>{icon}</span>}
      <span className="min-w-0 truncate">{children}</span>
    </Dropdown.Item>
  );
}

function MenuSubTrigger({ icon, className, children, ...rest }: Dropdown.DropdownMenuSubTriggerProps & { icon?: ReactNode }) {
  return (
    <Dropdown.SubTrigger {...rest} className={cx(itemClass, 'text-n10 data-[state=open]:bg-n3', className)}>
      {icon && <span className="text-n8 shrink-0 inline-flex">{icon}</span>}
      <span className="min-w-0 truncate flex-1">{children}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-n7"><path d="m9 6 6 6-6 6" /></svg>
    </Dropdown.SubTrigger>
  );
}

function MenuLabel({ className, children, ...rest }: Dropdown.DropdownMenuLabelProps) {
  return <Dropdown.Label {...rest} className={cx('px-3 pt-1.5 pb-1 text-micro text-n7 truncate', className)}>{children}</Dropdown.Label>;
}

function MenuSeparator({ className, ...rest }: Dropdown.DropdownMenuSeparatorProps) {
  return <Dropdown.Separator {...rest} className={cx('h-px bg-n4 my-1 mx-1', className)} />;
}

/** A dropdown menu styled with the tokens: Menu.Root › Menu.Trigger + Menu.Content › Menu.Item (and Sub, Label, Separator). */
export const Menu = {
  Root: Dropdown.Root,
  Trigger: Dropdown.Trigger,
  Content: MenuContent,
  Item: MenuItem,
  Label: MenuLabel,
  Separator: MenuSeparator,
  Sub: Dropdown.Sub,
  SubTrigger: MenuSubTrigger,
  SubContent: MenuSubContent,
};
